// services/astro-bot-miniapp/src/screens/onboarding/PickPersona.tsx
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../lib/api-client.js';
import { useAuth } from '../../store/auth.js';
import { CosmosBackdrop } from '../../components/brand/CosmosBackdrop.js';
import { OnboardingProgressBar } from '../../components/onboarding/OnboardingProgressBar.js';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import {
  PersonaCarousel,
  type PersonaCarouselLabels,
  type PersonaFrame,
} from '../../components/onboarding/index.js';
import styles from './PickPersona.module.css';

interface Frame { r2Key: string; url: string; archetype?: string }
interface Resp {
  generic: { status: 'pending' | 'ready' | 'failed'; startedAt?: string | null; frames: Frame[] };
  selfie: { status: string; frames: Frame[] };
  selectedR2Key: string | null;
}

const TYPICAL = 55; // seconds — conservative; bar caps at 95% then goes indeterminate

export function PickPersona(): JSX.Element {
  const { t } = useTranslation('flows');
  const { t: tc } = useTranslation('common');
  const jwt = useAuth(s => s.jwt);
  const nav = useNavigate();
  const [status, setStatus] = useState<'loading' | 'pending' | 'ready' | 'failed'>('loading');
  const [frames, setFrames] = useState<Frame[]>([]);
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const mountRef = useRef<number>(Date.now());
  const [nowMs, setNowMs] = useState<number>(Date.now());

  // Poll star-persona until terminal (ready/failed). Mirrors AddingToWorld cleanup.
  useEffect(() => {
    if (!jwt) return;
    let cancelled = false;
    const tick = async (): Promise<void> => {
      try {
        const r = await apiFetch('/api/v1/star-persona', jwt);
        if (!r.ok) return;
        const j = (await r.json()) as Resp;
        if (cancelled) return;
        const g = j.generic;
        setStartedAt(g.startedAt ?? null);
        if (g.status === 'ready') {
          const generic = (g.frames ?? []).slice(0, 9);
          setFrames(generic);
          if (j.selectedR2Key) {
            const idx = generic.findIndex(f => f.r2Key === j.selectedR2Key);
            if (idx >= 0) setActive(idx);
          }
          setStatus('ready');
        } else if (g.status === 'failed') {
          setStatus('failed');
        } else {
          setStatus('pending');
        }
      } catch (err) {
        console.error('[PickPersona] poll error', err);
      }
    };
    void tick();
    const id = setInterval(tick, 1500);
    return () => { cancelled = true; clearInterval(id); };
  }, [jwt]);

  // 1s ticker for a smooth ETA countdown between 1.5s polls (pending only).
  useEffect(() => {
    if (status !== 'pending') return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [status]);

  // All onboarding exits advance the step (select or skip → step 9) then route
  // to push-consent — never /home, which would leave step 4 and bounce the user
  // back to info-reveal on next open.
  async function leaveToPush(): Promise<void> {
    if (submitting) return;
    setSubmitting(true);
    try {
      if (jwt && frames.length > 0) {
        await apiFetch('/api/v1/star-persona/select', jwt, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ r2Key: frames[active].r2Key }),
        });
      } else if (jwt) {
        await apiFetch('/api/v1/star-persona/skip', jwt, { method: 'POST' });
      }
    } catch {
      // ignore — navigate anyway
    } finally {
      nav('/onboarding/push', { replace: true });
    }
  }

  function addSelfie(): void { nav('/onboarding/selfie'); }

  const carouselFrames: PersonaFrame[] = useMemo(
    () => frames.map((f) => ({ r2Key: f.r2Key, url: f.url, label: f.archetype })),
    [frames],
  );
  const labels: PersonaCarouselLabels = useMemo(
    () => ({
      overlay: t('onboarding.pickPersona.overlay'),
      ctaMinipack: t('onboarding.pickPersona.cta.minipack', { stars: SKU_CATALOG['avatar_mini'].stars }),
      chipMax: t('onboarding.pickPersona.chip.max', { stars: SKU_CATALOG['avatar_max'].stars }),
      chipSavePreviews: t('onboarding.pickPersona.chip.savePreviews'),
    }),
    [t],
  );

  if (status === 'loading') {
    return <div className={styles.loading}>{tc('states.loading')}</div>;
  }

  if (status === 'ready') {
    return (
      <div className={styles.screen}>
        <CosmosBackdrop />
        <OnboardingProgressBar step={4} etaSeconds={10} />
        <h1 className={styles.title}>{t('onboarding.pickPersona.title')}</h1>
        <PersonaCarousel
          frames={carouselFrames}
          tier="mini"
          labels={labels}
          selectedIndex={active}
          onSelectFrame={setActive}
          onCtaPress={addSelfie}
        />
        <button
          type="button"
          className={styles.ctaSecondary}
          onClick={() => void leaveToPush()}
          disabled={submitting}
        >
          {t('onboarding.pickPersona.cta.skip')}
        </button>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className={styles.screen}>
        <CosmosBackdrop />
        <h1 className={styles.title}>{t('onboarding.pickPersona.failed.title')}</h1>
        <p className={styles.pendingHint}>{t('onboarding.pickPersona.failed.body')}</p>
        <button
          type="button"
          className={styles.ctaSecondary}
          onClick={() => void leaveToPush()}
          disabled={submitting}
        >
          {t('onboarding.pickPersona.cta.skipForNow')}
        </button>
      </div>
    );
  }

  // pending — honest, server-anchored ETA (cap 95%, indeterminate on overshoot)
  const base = startedAt ? Date.parse(startedAt) : mountRef.current;
  const elapsed = Math.max(0, (nowMs - base) / 1000);
  const overshoot = elapsed >= TYPICAL;
  const progressPct = Math.min(95, (elapsed / TYPICAL) * 100);
  const etaSec = Math.max(1, Math.round(TYPICAL - elapsed));
  const almost = overshoot || etaSec <= 5;

  return (
    <div className={styles.screen}>
      <CosmosBackdrop />
      <h1 className={styles.title}>{t('onboarding.pickPersona.pending.title')}</h1>
      <OnboardingProgressBar
        progressPct={overshoot ? 95 : progressPct}
        indeterminate={overshoot}
        label={almost
          ? t('onboarding.pickPersona.pending.almostReady')
          : t('onboarding.progress.etaSeconds', { seconds: etaSec })}
      />
      <button
        type="button"
        className={styles.ctaSecondary}
        onClick={() => void leaveToPush()}
        disabled={submitting}
      >
        {t('onboarding.pickPersona.cta.skipForNow')}
      </button>
    </div>
  );
}
